import { Button } from "@/components/ui/button"
import { usePayment } from "../use-payment"
import { toast } from "sonner"
import { useOfflinePayment } from "./hooks/use-offline"
import { Loader2 } from "lucide-react"
import { useFormStore } from "@/stores/use-form-store"
import { getPeriodType, getPlanType } from "../utils"
import { useMemo, useState } from "react"
import ConfirmPopUpModal from "./components/confirm-pop-up"

const OfflinePaymentActions = ({ periodNumber }: { periodNumber: number }) => {
	const {
		feeSummary,
		activeLoanPlans,
		activeSelfPlans,
		selectedPaymentPlan,
		couponDiscount,
		getValues,
		course_id,
		activeTab,
		formState,
		selfOfflinePaymentForm: { form: selfOfflinePaymentForm },
		loanOfflinePaymentForm: { form: loanOfflinePaymentForm },
	} = usePayment()
	const { stage_check_in_time } = getValues()
	const [isModalOpen, setIsModalOpen] = useState(false)
	const { applicationId, config } = useFormStore()
	const { useSubmitOfflinePayment, useSubmitOfflineLoan } =
		useOfflinePayment()
	const {
		mutateAsync: submitOfflinePayment,
		isPending: isSelfPaymentPending,
	} = useSubmitOfflinePayment()
	const { mutateAsync: submitOfflineLoan, isPending: isLoanPaymentPending } =
		useSubmitOfflineLoan()
	const isPending = isSelfPaymentPending || isLoanPaymentPending

	const selectedOrderItems = useMemo(() => {
		if (!feeSummary) return []

		return feeSummary.flatMap(item => {
			if (item.isDiscountComponent) return []
			return item.price_details?.map(price => {
				return {
					fee_component_id: item.component_id,
					component_dimension_id: item.dimension_id,
					amount: price.final_price,
					currency: item.currency,
					period_type: getPeriodType(item.frequency),
					period_number: price.period_number,
				}
			})
		})
	}, [feeSummary])
	const adjustments = useMemo(() => {
		if (
			!couponDiscount ||
			couponDiscount.discount === 0 ||
			(selectedPaymentPlan.startsWith("self") &&
				couponDiscount.plan === "LOAN") ||
			(selectedPaymentPlan.startsWith("loan") &&
				couponDiscount.plan !== "LOAN")
		)
			return []

		const fee = feeSummary.find(
			fee => fee.coupon_discount && fee.coupon_discount > 0,
		)

		if (!fee || !fee.coupon_discount || fee.coupon_discount === 0) return []

		const temp = [
			{
				amount: fee.coupon_discount,
				currency: fee.currency,
				type: "DISCOUNT",
				description: `{${couponDiscount.coupon_id}}-coupon applied`,
			},
		]
		return temp.map(item => {
			return {
				amount: item.amount,
				currency: item.currency,
				type: item.type,
				description: item.description,
			}
		})
	}, [couponDiscount, feeSummary])
	const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		let isValid = false
		console.log("formState: ", formState)
		if (formState.payment_type === "self") {
			console.log(
				"selfOfflinePaymentForm: ",
				selfOfflinePaymentForm.formState,
			)
			isValid = await selfOfflinePaymentForm.trigger()
		} else {
			console.log(
				"loanOfflinePaymentForm: ",
				loanOfflinePaymentForm.formState,
			)
			isValid = await loanOfflinePaymentForm.trigger()
		}
		if (!isValid) {
			toast.error("Please fill all the required fields")
			return
		}
		console.log(
			"Offline Payment Form : ",
			formState.payment_type === "self"
				? selfOfflinePaymentForm.getValues()
				: loanOfflinePaymentForm.getValues(),
		)
		setIsModalOpen(true)
	}

	const handleConfirmClick = async () => {
		if (
			formState?.payment_type === "self" &&
			formState?.self_payment_mode === "already-paid"
		) {
			await submitOfflinePayment({
				reference_number:
					selfOfflinePaymentForm.getValues().transaction_reference ||
					"",
				account_holder_name:
					selfOfflinePaymentForm.getValues().account_holder_name,
				amount: selfOfflinePaymentForm.getValues().amount_paid,
				currency: feeSummary[0]?.currency || "INR",
				mode: selfOfflinePaymentForm.getValues().payment_mode,
				bank_name: selfOfflinePaymentForm.getValues().bank_name,
				remarks: selfOfflinePaymentForm.getValues()?.remarks || "",
				paid_at: selfOfflinePaymentForm.getValues().payment_date,
				order: {
					semester: periodNumber || 1,
					additional_info: {},
					application_id: applicationId || "",
					batch_id: config?.batch_id || "",
					course_id: course_id,
					fee_grid_revision_id: feeSummary[0]?.revision_id,
					category: "TUITION",
					plan: getPlanType(
						(activeTab.includes("self")
							? activeSelfPlans
							: activeLoanPlans
						)?.find(
							plan =>
								plan.component_id ===
								selectedPaymentPlan.replace(
									/^(self|loan)-/,
									"",
								),
						)?.frequency || "",
					),
					// period_number: 1,
					currency: feeSummary[0]?.currency,
					mode: activeTab?.includes("self") ? "SELF" : "LOAN",
					method: "OFFLINE",
					created_at: stage_check_in_time,
					items: selectedOrderItems,
					adjustments: adjustments,
				},
				proof_url: selfOfflinePaymentForm.getValues().proof_of_payment,
				additional_info: {
					plan_id: selectedPaymentPlan.replace(/^(self|loan)-/, ""),
				},
			}).then(() => {
				setIsModalOpen(false)
			})
		} else if (
			formState?.payment_type === "loan" &&
			formState?.loan_payment_mode === "loan-disbursed"
		) {
			const loanFormValues = loanOfflinePaymentForm.getValues()
			await submitOfflineLoan({
				lender_loan_id: loanFormValues.loan_id,
				lender: loanFormValues.loan_vendor,
				loan_amount: loanFormValues.requested_loan_amount,
				currency: feeSummary[0]?.currency || "INR",
				proof_url: loanFormValues.proof_of_loan_disbursal,
				status: "PENDING_VERIFICATION",
				applicant_name: loanFormValues.applicant_name,
				applicant_mobile_number:
					loanFormValues.applicant_mobile.toString(),
				applicant_email: loanFormValues.applicant_email,
				applicant_relation: loanFormValues.applicant_relation,
				remarks: loanFormValues.remarks || "",
				order: {
					semester: periodNumber || 1,
					additional_info: {},
					application_id: applicationId || "",
					batch_id: config?.batch_id || "",
					course_id: course_id,
					fee_grid_revision_id: feeSummary[0]?.revision_id,
					category: "TUITION",
					plan: getPlanType(
						activeLoanPlans?.find(
							plan =>
								plan.component_id ===
								selectedPaymentPlan.replace(
									/^(self|loan)-/,
									"",
								),
						)?.frequency || "",
					),
					// period_number: 1,
					currency: feeSummary[0]?.currency || "INR",
					mode: "LOAN",
					method: "OFFLINE",
					created_at: stage_check_in_time,
					items: selectedOrderItems,
					adjustments: adjustments,
				},
				additional_info: {
					plan_id: selectedPaymentPlan.replace(/^(self|loan)-/, ""),
				},
			}).then(() => {
				setIsModalOpen(false)
			})
		}
	}
	const messages = [
		`Once you submit, your ${formState.payment_type === "self" ? "payment" : "loan application"} details will be sent to the university’s finance team for verification.`,
		`Please review your information carefully before proceeding.`,
	]
	const validateTransactionReferencePending =
		useOfflinePayment().useValidateTransactionReference({
			reference_number:
				selfOfflinePaymentForm.watch("transaction_reference") || "",
		}).isFetching
	return (
		<>
			<ConfirmPopUpModal
				isDisabled={isPending}
				confirmTitle={
					<div className="flex items-center justify-center gap-2">
						<span>Yes, Submit</span>
						{isPending && <Loader2 className="animate-spin" />}
					</div>
				}
				confirmAction={handleConfirmClick}
				isModalOpen={isModalOpen}
				setIsModalOpen={setIsModalOpen}
				title="Confirm Submission"
				messages={messages}
			/>
			<Button
				onClick={handleClick}
				disabled={
					isPending ||
					validateTransactionReferencePending ||
					!(formState.payment_type === "self"
						? selfOfflinePaymentForm.formState.isValid
						: loanOfflinePaymentForm.formState.isValid)
				}
				type="button"
				className="mt-9 flex w-full items-center justify-center gap-2 rounded-2xl bg-green-600 text-white hover:bg-green-500"
			>
				<svg
					width="20"
					viewBox="0 0 48 48"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g
						fill="none"
						stroke="currentColor"
						strokeWidth="4"
						strokeLinejoin="round"
					>
						<path d="M4 10a2 2 0 0 1 2-2h36a2 2 0 0 1 2 2v28a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10z" />
						<path
							d="M4 16h40"
							strokeLinecap="square"
						/>
						<path
							d="M27 32h9"
							strokeLinecap="round"
						/>
						<path
							d="M44 10v16"
							strokeLinecap="round"
						/>
						<path
							d="M4 10v16"
							strokeLinecap="round"
						/>
					</g>
				</svg>
				<span className="flex items-center justify-center gap-2">
					<span>
						{formState.payment_type === "loan"
							? "Submit Loan Details"
							: "Submit Payment Details"}
					</span>
					{isPending && <Loader2 className="animate-spin" />}
				</span>
			</Button>
		</>
	)
}

export default OfflinePaymentActions
